import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  formatUsPhoneInput,
  isValidUsPhoneNumber,
} from '../utils/usPhone'

/**
 * SMS opt-in dialog. Controlled visibility via `open` / `onClose`.
 */
function SmsOptInModal({ open, onClose }) {
  const [phone, setPhone] = useState('')
  const [smsConsent, setSmsConsent] = useState(false)
  const [formError, setFormError] = useState('')
  const [phoneInvalid, setPhoneInvalid] = useState(false)
  const [smsComplete, setSmsComplete] = useState(false)
  const [smsEnrolled, setSmsEnrolled] = useState(false)

  useEffect(() => {
    if (!open) return
    setPhone('')
    setSmsConsent(false)
    setFormError('')
    setPhoneInvalid(false)
    setSmsComplete(false)
    setSmsEnrolled(false)
  }, [open])

  const handlePhoneChange = (e) => {
    setPhone(formatUsPhoneInput(e.target.value))
    setPhoneInvalid(false)
    if (formError) setFormError('')
  }

  const handleSmsSubmit = (e) => {
    e.preventDefault()
    setFormError('')
    setPhoneInvalid(false)

    if (smsConsent && !isValidUsPhoneNumber(phone)) {
      setPhoneInvalid(true)
      setFormError(
        'Enter a valid U.S. phone number (10 digits). Area codes and prefixes cannot start with 0 or 1.'
      )
      return
    }

    setSmsEnrolled(Boolean(smsConsent))
    setSmsComplete(true)
  }

  if (!open) return null

  return (
    <div
      className="sms-modal-backdrop"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="sms-opt-in-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sms-modal-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <button
          type="button"
          className="btn-close sms-modal-close"
          aria-label="Close"
          onClick={onClose}
        />
        <h2 id="sms-modal-title" className="sms-modal-heading">
          SMS updates from Redi Platforms
        </h2>

        {smsComplete ? (
          <div className="sms-modal-body">
            <p className="sms-modal-success-text mb-0">
              {smsEnrolled ? (
                <>
                  Thank you for signing up for SMS updates from Redi Platforms.
                  We’ll be in touch at the number you provided.
                </>
              ) : (
                <>
                  Thank you. You were not subscribed to SMS because the consent
                  box was not selected. You can close this window, or check the
                  box and submit again if you would like to receive text messages.
                </>
              )}
            </p>
            <button
              type="button"
              className="btn sms-modal-btn-primary mt-3"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        ) : (
          <form className="sms-modal-body" onSubmit={handleSmsSubmit} noValidate>
            <p className="sms-modal-lead">
              If you choose to opt in, you may receive SMS messages from Redi
              Platforms at the number you provide. Types of messages may include:{' '}
              informational messages (such as service notices and
              account-related updates). Message frequency varies.
            </p>
            <p className="sms-modal-lead">
              Enter your U.S. mobile number below. Consent is optional—you can
              submit without checking the box.
            </p>

            <div className="sms-field-group">
              <label htmlFor="sms-phone" className="sms-field-label">
                Mobile phone number
              </label>
              <input
                id="sms-phone"
                name="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                className={`sms-input ${phoneInvalid ? 'is-invalid' : ''}`}
                placeholder="(555) 555-5555"
                value={phone}
                onChange={handlePhoneChange}
                aria-invalid={phoneInvalid}
                aria-describedby={formError ? 'sms-form-error' : undefined}
              />
              <p className="sms-field-hint">
                U.S. numbers only — full 10-digit number (area code cannot start
                with 0 or 1).
              </p>
            </div>

            <div className="sms-consent-block">
              <div className="sms-consent-row">
                <input
                  id="sms-only-consent"
                  name="smsConsentOnly"
                  type="checkbox"
                  className="form-check-input sms-consent-checkbox"
                  checked={smsConsent}
                  onChange={(e) => {
                    setSmsConsent(e.target.checked)
                    if (formError) setFormError('')
                  }}
                />
                <label
                  className="sms-consent-label"
                  htmlFor="sms-only-consent"
                >
                  <span className="sms-consent-optional">Optional.</span>{' '}
                  By checking this box, you agree to receive text messages from
                  Redi Platforms related to Customer care and account notification
                  at the phone number provided above. You may reply STOP to opt-out
                  at any time. Reply HELP for assistance. Messages and data rates
                  may apply. Message frequency will vary.
                </label>
              </div>
              <p className="sms-consent-policy-links mb-0">
                Learn more on our{' '}
                <a
                  href="https://maderedi.com/privacy"
                  className="sms-modal-inline-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                Page and{' '}
                <a
                  href="https://maderedi.com/terms"
                  className="sms-modal-inline-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms &amp; conditions
                </a>
              </p>
            </div>

            <p className="sms-modal-disclosure">
              Message and data rates may apply. Message frequency varies. Reply
              STOP to unsubscribe from SMS, or HELP for help. See our{' '}
              <Link to="/privacy" className="sms-modal-inline-link">
                Privacy Policy
              </Link>{' '}
              for how we collect, use, and share personal information, and our{' '}
              <Link to="/terms" className="sms-modal-inline-link">
                Terms &amp; Conditions
              </Link>{' '}
              for rules that apply to our SMS program. If we share data with
              service providers, we do not sell or share your SMS opt-in or your
              phone number for SMS with third parties for their own marketing.
            </p>

            {formError && (
              <p id="sms-form-error" className="sms-form-error" role="alert">
                {formError}
              </p>
            )}

            <div className="sms-modal-actions">
              <button
                type="button"
                className="btn sms-modal-btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn sms-modal-btn-primary">
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default SmsOptInModal
